<template>
  <div class="tq2-preview-panel" :data-testid="tid('tq2-preview-panel')">
    <!-- ── head: title + the loading bar ─────────────────────────────────────
         The bar lives here, ABOVE the card, and never in place of it: a re-render
         that blanked the column would make every keystroke flash the panel empty
         (see the `applyResult` comment). It also has to be visible while there is
         no card at all, which is why it is not overlaid inside the card box.
         🔴 `compact` drops the title and the caveat, NEVER the bar: a card rendered
         by a round-trip the reader cannot see just appears late, and in the table
         a dozen panels are waiting at once. -->
    <div class="tq2-preview-head" :class="{ 'tq2-preview-head--compact': compact }">
      <div v-if="!compact" class="tq2-preview-title">
        <v-icon size="16" class="mr-1">mdi-card-account-details-outline</v-icon>
        ตัวอย่างการ์ด
      </div>
      <div v-if="!compact" class="tq2-preview-hint">ประกอบจาก<b>ข้อมูลตัวอย่าง</b> ไม่ใช่ข้อมูลลูกค้าจริง</div>
      <v-progress-linear
        :active="loading"
        indeterminate
        height="3"
        rounded
        color="primary"
        class="tq2-preview-bar"
        :data-testid="tid('tq2-preview-loading')"
      />
    </div>

    <!-- อ่าน config/template ไม่ได้ — เตือนไว้เหนือการ์ด แต่การ์ดใบเดิมยังอยู่ -->
    <v-alert
      v-if="errors.length"
      type="warning"
      variant="tonal"
      density="compact"
      class="tq2-preview-alert"
      :data-testid="tid('tq2-preview-error')"
    >
      <div v-for="(msg, i) in errors" :key="i" class="text-caption">{{ msg }}</div>
    </v-alert>

    <!-- 🔴 UNSUBMITTABLE, NOT INERT — no `interactive`, no `busy`, no `@card-submit`, so the
         CTAs are drawn and dead: a preview card that could be submitted would post a
         synthetic answer nowhere.
         🔴 But `preview` DOES unlock the simulation's sliders, because on the phone dragging
         one sends nothing either — the thumbs, the three-colour ceiling bars and the running
         total all move in the client and only the CTA ever talks to the server. An author
         who cannot drag is not previewing the customer's card, they are previewing its first
         frame, and the borrowed slice, the over-ceiling swap and the substituted
         `overCapNote` are precisely the things that only appear once something moves. -->
    <div v-if="card" class="tq2-preview-card" :data-testid="tid('tq2-preview-card')">
      <TaxSummaryCardV2 :card="card" :raw="raw" preview />
    </div>

    <div
      v-else-if="!loading"
      class="tq2-preview-empty text-caption"
      :data-testid="tid('tq2-preview-empty')"
    >
      ยังไม่มีการ์ดให้แสดง — ตั้งค่าการ์ดแล้วจะขึ้นที่นี่
    </div>

    <!-- «JSON ที่ render จริง» — the payload the backend assembled, verbatim. Folded
         away because it is evidence, not the deliverable: the card above is. And in
         `compact` it is gone entirely: evidence one drawer deep is worth having beside
         the boxes you are typing in, but stacked once per table row it buries the very
         cards the reader opened the page to compare. -->
    <v-expansion-panels v-if="raw && !compact" variant="accordion" class="tq2-preview-json-panel mt-3">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-icon size="16" class="mr-2">mdi-code-json</v-icon>
          JSON ที่ render จริง
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="tq2-preview-json-bar">
            <v-btn
              size="x-small"
              variant="text"
              prepend-icon="mdi-content-copy"
              :data-testid="tid('tq2-preview-json-copy')"
              @click="copyJson"
            >คัดลอก</v-btn>
            <span v-if="copyState" class="text-caption tq2-preview-copy-note">{{ copyState }}</span>
          </div>
          <pre class="tq2-preview-json" :data-testid="tid('tq2-preview-json')">{{ raw }}</pre>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
// Live card preview for the Tax Question V2 item editor (T7).
//
// Shows the card THIS item's half-typed config/template would produce, rendered by the real
// backend pipeline (projector + template engine + card builders) against synthetic sample
// answers. Nothing here interprets a card: it hands the server the boxes the author is typing
// in and hands `TaxSummaryCardV2` what comes back.
//
// 🔴 STRICTLY READ-ONLY ON `item`. The dialog normalizes its item in `resetForm()` /
// `onKindChange()` and nowhere else; normalizing per keystroke has broken that file twice.
// This panel only ever READS `item`, and emits nothing.
//
// ── The two rules that shape the whole file ──────────────────────────────────────────────
//
// 1. A LATE ANSWER IS NOT AN ANSWER. Requests are debounced but still overlap — a 400 ms
//    pause mid-word fires one, the next keystroke fires another, and the first can land
//    second (`errors` from a document the author already fixed, over a card built from the
//    text now on screen). Every request takes a `reqSeq` ticket and a reply that is not
//    holding the newest ticket is dropped in silence.
//
// 2. AN ERROR NEVER CLEARS THE LAST GOOD CARD. Typing JSON means being mid-edit, i.e.
//    unparseable, several times per line. Blanking the column on each of those would strobe
//    the panel and leave the author with nothing to compare their edit against; the warning
//    goes ABOVE the card the last successful render produced, and that card stays put.
import TaxSummaryCardV2 from './TaxSummaryCardV2.vue';
import { buildPreviewRequest, previewSignature, parsePreviewCard } from '../utils/taxCardPreview';
import { taxQuestionV2Api } from '../services/taxQuestionV2Api';

/**
 * How long the author has to stop typing before the card re-renders.
 * Long enough that a word costs one round-trip instead of eight, short enough to still read
 * as "live" (the feature was asked for as realtime — there is no refresh button to fall back on).
 */
const DEBOUNCE_MS = 400;

/** How long the copy button's outcome note stays on screen. */
const COPY_NOTE_MS = 1500;

export default {
  name: 'TaxCardPreviewPanelV2',
  components: { TaxSummaryCardV2 },
  props: {
    /** The dialog's live item. READ-ONLY — see the file header. */
    item: { type: Object, required: true },
    /**
     * The SHORT_FORM JSON box exactly as typed. Preferred over `item.shortFormConfig` by
     * `buildPreviewRequest`, so that the preview shows the document on screen rather than
     * the last one that happened to parse.
     */
    shortFormConfigText: { type: String, default: '' },
    /**
     * Which member of the copy bags to render (`th` / `en` / `zh`). The backend collapses every bag to it and,
     * when a seat has Thai but not this language, renders the whole card in Thai and says so in `errors`.
     * Part of the request signature, so switching it re-renders.
     */
    lang: { type: String, default: 'th' },
    /**
     * Is this panel actually on screen? `false` means NO network traffic at all — the dialog
     * mounts this for card kinds only, but it also stays mounted while the dialog is closed.
     */
    active: { type: Boolean, default: false },
    /**
     * Strip the panel down to what is ABOUT THIS CARD, for a preview embedded in something
     * that already has its own frame — the question table, where a dozen of these sit one
     * under another. The dialog's chrome (a heading naming the pane, the sample-data caveat,
     * the JSON evidence drawer and its copy button) is said once per panel, so in the table it
     * is said a dozen times and drowns the cards themselves. What is kept is what differs from
     * row to row: the loading bar, the Thai warning, the card, the empty state.
     *
     * 🔴 Default `false` — the dialog does not pass this and must keep every part it has today.
     */
    compact: { type: Boolean, default: false },
    /**
     * Appended to THIS panel's own `data-testid`s. `TaxSummaryCardV2` hard-codes its testids
     * with no prefix at all, so N cards on one page already collide N ways and that file (5k
     * lines, shared with chat / log / mutual fund) is not worth reopening for it. Making the
     * panel around each card addressable per item is enough to scope a locator down to one row.
     * Empty by default, so the dialog's single panel keeps byte-identical testids.
     */
    testidSuffix: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      /** Envelope `{ type, version, refId, data }` of the last SUCCESSFUL render. */
      card: null,
      /** That same card's JSON text, straight from the server — never re-stringified here,
       *  because `TaxSummaryCardV2` watches `raw` to re-seed its internal state and a value
       *  recomputed every tick would reset the card's accordions/sliders continuously. */
      raw: '',
      cardType: null,
      /** Thai messages from the server (or from a failed call) about the CURRENT attempt. */
      errors: [],
      /** Did the current attempt fail to produce a card? (`card` may still hold an older one.) */
      failed: false,
      /** Debounce handle — cleared on every reschedule and in `beforeUnmount`. */
      timer: null,
      /** Ticket of the newest request in flight; see rule 1. */
      reqSeq: 0,
      /** Signature already reflected on screen — re-asking for it would spend a round-trip
       *  to redraw the identical card (and re-seed the card component's state for nothing). */
      renderedSignature: null,
      copyState: '',
      copyTimer: null,
    };
  },
  computed: {
    /** The POST body, or `null` when this kind has no card to preview. */
    request() {
      return buildPreviewRequest(this.item, { shortFormConfigText: this.shortFormConfigText, lang: this.lang });
    },
    /** "Has the CARD changed?" — byte-identical while unrelated fields are edited. */
    signature() {
      return previewSignature(this.request);
    },
  },
  watch: {
    // Opening the panel must not cost the author a 400 ms stare at an empty column, so the
    // first render is scheduled with no delay. `immediate` covers the case where the panel is
    // mounted already active.
    active: {
      immediate: true,
      handler(on) {
        if (!on) {
          this.cancelPending();
          return;
        }
        this.schedule(0);
      },
    },
    // Every keystroke in a card box lands here; the debounce is what makes that affordable.
    signature() {
      this.schedule(DEBOUNCE_MS);
    },
  },
  methods: {
    /** `data-testid` for one of this panel's own nodes — see the `testidSuffix` prop. */
    tid(base) {
      return this.testidSuffix ? base + this.testidSuffix : base;
    },
    /** Drop the pending debounce (idempotent). Does NOT cancel a request already in flight —
     *  that one is neutralised by its stale ticket instead. */
    cancelPending() {
      if (this.timer !== null) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    },
    /** Queue a render `delay` ms from now, replacing whatever was queued before. */
    schedule(delay) {
      this.cancelPending();
      if (!this.active || !this.request) return;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.fetchPreview();
      }, delay);
    },
    async fetchPreview() {
      const req = this.request;
      // Re-checked here and not only in `schedule`: the dialog can close, or the kind can
      // change to one with no card, during the debounce window.
      if (!this.active || !req) return;
      const sig = this.signature;
      // Already on screen and it worked — nothing to ask for. A signature that FAILED is asked
      // again though: reopening the panel is the only retry an author has for a call that died
      // on the transport rather than on their document.
      if (sig === this.renderedSignature && !this.failed) return;

      const seq = this.reqSeq + 1;
      this.reqSeq = seq;
      this.loading = true;
      try {
        const data = await taxQuestionV2Api.previewCard(req);
        if (seq !== this.reqSeq) return; // rule 1 — a newer request owns the panel now
        this.renderedSignature = sig;
        this.applyResult(data);
      } catch (err) {
        if (seq !== this.reqSeq) return;
        // A failed CALL is not a failed document: the author may not have typed anything
        // wrong at all (session expired, backend down). Say so in its own words and, per
        // rule 2, leave the card standing.
        this.renderedSignature = sig;
        this.failed = true;
        this.errors = ['เรียก preview ไม่สำเร็จ: ' + ((err && err.message) || 'ไม่ทราบสาเหตุ')];
      } finally {
        if (seq === this.reqSeq) this.loading = false;
      }
    },
    /**
     * Fold one server result into the panel.
     *
     * 🔴 Rule 2 lives here: `card`/`raw`/`cardType` are only ever OVERWRITTEN by a render that
     * produced a card. A result that produced none updates `errors` and `failed` and nothing else.
     *
     * 🔴 THE CARD IS PARSED OUT OF `raw`; THE RESPONSE CARRIES NO CARD OBJECT. That is deliberate
     * and is how every other card in the product reaches this SPA (`taxCards` is a `string?` on
     * both turn DTOs, read with `utils/taxCardMarker.js`). The endpoint used to send a live
     * Newtonsoft `JObject` through a System.Text.Json pipeline, which flattened every scalar leaf
     * to `[]` — see the note over `parsePreviewCard`. One representation on the wire, parsed once,
     * here.
     *
     * `raw` is stored as the SERVER'S string, verbatim and never re-stringified from the parsed
     * object: `TaxSummaryCardV2` watches it to re-seed its accordions and sliders, so a value
     * recomputed per render would reset the card's state under the author's hands.
     */
    applyResult(data) {
      const result = data && typeof data === 'object' ? data : {};
      const errors = Array.isArray(result.errors) ? result.errors.filter((m) => m) : [];
      this.errors = errors.map((m) => String(m));

      const raw = typeof result.raw === 'string' ? result.raw : '';
      const { card, error } = parsePreviewCard(raw);
      if (card) {
        this.card = card;
        this.raw = raw;
        this.cardType = result.cardType || null;
        this.failed = false;
      } else {
        // No `raw` at all ⇒ the server already said why in `errors`; a `raw` this side cannot read
        // is a second thing worth saying. Either way the last good card stays on screen.
        if (error) this.errors = this.errors.concat([error]);
        this.failed = true;
      }
    },
    /**
     * Copy the rendered JSON. GUARDED: `navigator.clipboard` is absent on http:// origins and
     * its write can be denied outright — neither may surface as an unhandled rejection, so the
     * author is told to copy by hand instead.
     */
    async copyJson() {
      const text = String(this.raw || '');
      if (!text) return;
      let ok = false;
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          ok = true;
        }
      } catch (_err) {
        ok = false; // not https / permission denied
      }
      this.noteCopy(ok ? 'คัดลอกแล้ว' : 'คัดลอกอัตโนมัติไม่ได้ — เลือกข้อความแล้วกด Ctrl/⌘ + C');
    },
    noteCopy(message) {
      if (this.copyTimer !== null) window.clearTimeout(this.copyTimer);
      this.copyState = message;
      this.copyTimer = window.setTimeout(() => {
        this.copyState = '';
        this.copyTimer = null;
      }, COPY_NOTE_MS);
    },
  },
  beforeUnmount() {
    this.cancelPending();
    if (this.copyTimer !== null) {
      window.clearTimeout(this.copyTimer);
      this.copyTimer = null;
    }
    // Anything still in flight answers to a ticket that can no longer be the newest.
    this.reqSeq += 1;
  },
};
</script>

<style scoped>
/* 🔴 NO height / overflow here. The column that hosts this panel owns the scrolling
   (`.tq2-preview-col` in `TaxQuestionItemV2Dialog.vue`); a second scroller nested inside it
   would trap the wheel and hide the card's own bottom. The JSON `<pre>` is the one exception. */
.tq2-preview-panel {
  min-width: 0;
}

.tq2-preview-head {
  margin-bottom: 8px;
}

.tq2-preview-title {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.tq2-preview-hint {
  margin-top: 2px;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq2-preview-bar {
  margin-top: 6px;
}

/* With the title and the caveat gone, the bar's clearance from them has nothing left to clear
   and reads as a stray gap over the card. NOTE: still no height/overflow — see the top. */
.tq2-preview-head--compact {
  margin-bottom: 4px;
}

.tq2-preview-head--compact .tq2-preview-bar {
  margin-top: 0;
}

.tq2-preview-alert {
  margin-bottom: 8px;
}

.tq2-preview-card {
  min-width: 0;
}

.tq2-preview-empty {
  padding: 24px 12px;
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 10px;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq2-preview-json-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.tq2-preview-copy-note {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* The rendered payload is read as JSON, so it must NOT be re-wrapped: a soft-wrapped line
   moves the indentation that says which key sits inside which. It scrolls sideways instead,
   and has the only height cap in this file so it cannot push the card off the column. */
.tq2-preview-json {
  margin: 0;
  max-height: 320px;
  overflow: auto;
  white-space: pre;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.82);
}
</style>
